// @flow
import styled from "styled-components";
import { startCase, lowerCase } from "lodash";
import { media } from "../../media";
import { Select } from "./Select";
import { Label } from "./Label";
import { SearchAreaSelect } from "./SearchAreaSelect";
import { usePropertyFilters, PROPERTY_TYPES } from "./usePropertyFilters";
import { type PropertyType } from "../PropertyCarousel/Property";

const propertyTypeOptions = PROPERTY_TYPES.map((t) => ({
  label: startCase(lowerCase(t.replace("_", " "))),
  value: t,
}));

const bedroomOptions = ["Studio", 1, 2, 3, 4, 5].map((n) => ({
  label: String(n),
  value: n === "Studio" ? 0 : n,
}));

const OuterPadding = styled.div`
  background-color: ${({ theme }) => theme.palette.hague130};
  padding: 50px 10px 40px;
  ${media.desktop`
    padding: 50px 20px 40px;
  `}
`;

const Wrapper = styled.div`
  width: 100%;
  max-width: 1140px;
  margin: auto;
  display: flex;
  flex-direction: column;
  ${media.tablet`
    flex-direction: row;
    flex-wrap: wrap;
  `}
  ${media.desktop`
    flex-wrap: nowrap;
  `}
`;

const LocationFilter = styled.div`
  flex-grow: 1;
  flex-shrink: 1;
  ${media.desktop`
    width: 25%;
    flex-shrink: unset;
    box-sizing: border-box;
    padding-right: 10px;
  `}
`;

const BedsFilter = styled.div`
  flex-shrink: 1;
  width: 100%;
  display: flex;
  ${media.desktop`
    width: 30%;
    flex-shrink: unset;
    padding: 0px 10px;
  `}
`;

const BedSelectRangeSeparator = styled(Label)`
  vertical-align: middle;
  align-self: flex-end;
  line-height: 54px;
  padding: 0px 10px;
`;

const BedSelect = styled.div`
  display: inline-block;
  flex-shrink: 1;
  flex-grow: 1;
`;

const PropertyTypeFilter = styled.div`
  ${media.tablet`
    width: 50%;
    padding-right: 10px;
    box-sizing: border-box;
  `}
  ${media.desktop`
    width: 25%;
    padding-left: 10px;
  `}
`;

const SortDirection = styled.div`
  flex-shrink: 1;
  flex-grow: 1;
  ${media.tablet`
    width: 50%;
    padding-left: 10px;
    box-sizing: border-box;
  `}
  ${media.desktop`
    width: 20%;
  `}
`;

const LoadingState = styled.div`
  opacity: ${({ loading }) => (loading ? "0.3" : "1")};
`;

type Props = {
  properties: PropertyType[],
  children(
    properties: PropertyType[],
    { loading?: boolean, resetFilters: () => void },
  ): React$Node,
};

export const Filters = ({ children, properties }: Props) => {
  const {
    sortDirection,
    setSortDirection,
    maxBeds,
    setMaxBeds,
    minBeds,
    setMinBeds,
    propertyType,
    setPropertyType,
    area,
    setArea,
    loading,
    filteredProperties,
    resetFilters,
  } = usePropertyFilters(properties);

  return (
    <>
      <OuterPadding>
        <Wrapper>
          <LocationFilter>
            <SearchAreaSelect
              value={area}
              onChange={(newArea) => {
                setArea(newArea);
              }}
            />
          </LocationFilter>
          <BedsFilter>
            <BedSelect>
              <Select
                data-test="min-beds-filter"
                label="Beds"
                placeholder="Min"
                value={minBeds}
                onChange={({ value }) => setMinBeds(value)}
                options={[
                  { label: "All", value: "ALL" },
                  ...bedroomOptions,
                  {
                    label: "6+",
                    value: 6,
                  },
                ]}
              />
            </BedSelect>
            <BedSelectRangeSeparator>to</BedSelectRangeSeparator>
            <BedSelect>
              <Select
                data-test="max-beds-filter"
                label="&nbsp;"
                placeholder="Max"
                value={maxBeds}
                onChange={({ value }) => setMaxBeds(value)}
                options={[
                  { label: "All", value: "ALL" },
                  ...bedroomOptions,
                  {
                    label: "6+",
                    value: 9,
                  },
                ]}
              />
            </BedSelect>
          </BedsFilter>
          <PropertyTypeFilter>
            <Select
              data-test="property-type-filter"
              label="Property type"
              placeholder="Select type"
              value={propertyType}
              onChange={({ value }) => setPropertyType(value)}
              options={propertyTypeOptions}
            />
          </PropertyTypeFilter>
          <SortDirection>
            <Select
              data-test="property-sort"
              label="Sort by price"
              placeholder="High to low"
              value={sortDirection}
              onChange={({ value }) => setSortDirection(value)}
              options={[
                { label: "High to low", value: "desc" },
                { label: "Low to high", value: "asc" },
              ]}
            />
          </SortDirection>
        </Wrapper>
      </OuterPadding>
      <LoadingState loading={loading}>
        {children(filteredProperties, { loading, resetFilters })}
      </LoadingState>
    </>
  );
};
